import {
  compose,
  lifecycle,
  mapProps,
  withHandlers,
  withStateHandlers,
} from 'recompose';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { withDataLayerProductDetailView } from '../Common/GTM/ECommerceTracking/withDataLayerProductDetailView';
import { ConfigurableProductPageBreadCrumb } from '../Common/BreadCrumb';
import { HTMLTrans, withTranslation } from '../Common/Translations';
import TermsAndConditions from '../Promotion/TermsAndConditions';
import { GalleryCarouselWithPreview } from '../Common/Carousel/Gallery';
import {
  withConfigurableProductData,
  addConfigurableProductToCart,
  fetchPcpProductConfigsData
} from '../Common/Providers/ConfigurableProductApi';
import ConfigurableProductForm from './ConfigurableProductForm';
import { validateFormField } from './FormFieldValidator';
import { showErrorMsg } from '../Common/ErrorMsg';
import AuthenticableAddToCartButton from './AddToCartButton';
import { roundCurrency } from '../Common/Helper';
import {
  getSelectedSku,
  getSkuUnitPrice,
  refreshVariableSteps,
} from './ConfigurableProductFormHelper';
import { FreeShippingTierBadge } from '../Common/FreeShippingTierBadge';

const ProductDetail = ({ details = [] }) => (
  <div className="product-details_field">
    <div className="configurable-product__form-field-title">
      <h2 className="ProximaNova-Bold text-primary text-uppercase">
        <HTMLTrans id="configurable_product.product_detail_title">PRODUCT DETAILS</HTMLTrans>
      </h2>
    </div>
    {
      details.map(({ header = '', value = '' }, index) => (
        <div key={`product-detail-${index}`} className="row product-details_content__row">
          <div className="col-12 col-sm-6 product-details_content__header">{header}</div>
          <div className="col-12 col-sm-6 product-details_content__value">{value}</div>
        </div>
      ))
    }
  </div>
);

ProductDetail.propTypes = {
  details: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string,
    value: PropTypes.string,
  })),
};

const ProductPromoInfo = ({ discountPercentage, promoCode, promoTnc = '<div></div>' }) => (
  <div className="action__promo">
    <span className="text-secondary text-uppercase">
      <strong>
        <HTMLTrans
          id="configurable_product.discount_percentage_text"
          variables={{ discountPercentage }}>
          {discountPercentage}% OFF
        </HTMLTrans>
      </strong>
    </span>
    <span>
      &nbsp;|&nbsp;<HTMLTrans id="configurable_product.use_code_text">Use Code</HTMLTrans>&nbsp;
    </span>
    <span className="text-secondary text-uppercase">
      <strong>"{promoCode}"</strong>
    </span>
    <div>
      <HTMLTrans id="configurable_product.disclaimer_text">Shipping and handling not
        included.</HTMLTrans>
    </div>
    <TermsAndConditions
      contents={promoTnc}
      id="configurable-product-tnc"
      tncText={<HTMLTrans id="configurable_product.tnc_text">T&amp;C applies</HTMLTrans>}/>
  </div>
);

ProductPromoInfo.propTypes = {
  discountPercentage: PropTypes.string.isRequired,
  promoCode: PropTypes.string.isRequired,
  promoTnc: PropTypes.string,
};

const ProductPriceAndButton = ({
  currency = '',
  handleOnClickCallToAction = () => null,
  onAuthenticated = () => null,
  unitPrice = 0,
  disable = false
}) => (
  <div className="action__price-and-button">
    <div className="price-and-button__price text-primary">
      {currency} {roundCurrency(unitPrice)}
    </div>
    <div className="price-and-button__button">
      <AuthenticableAddToCartButton
        onAuthenticated={onAuthenticated}
        onClick={handleOnClickCallToAction}
        disable={disable}
      />
    </div>
  </div>
);

ProductPriceAndButton.propTypes = {
  currency: PropTypes.string,
  handleOnClickCallToAction: PropTypes.func,
  onAuthenticated: PropTypes.func,
  unitPrice: PropTypes.number,
};

const ProductAction = ({
  currency,
  discountPercentage,
  handleOnClickCallToAction,
  onAuthenticated,
  promoCode,
  promoTnc,
  unitPrice,
  disable,
  lang
}) => (
  <div className="configurable-product__action">
    <div className="row">
      <div className="col-7 col-md-8 d-flex align-items-center">
        {
          discountPercentage && promoCode && <ProductPromoInfo
            discountPercentage={discountPercentage}
            promoCode={promoCode}
            promoTnc={promoTnc}/>
        }
      </div>
      <div className="col-5 col-md-4">
        <ProductPriceAndButton
          currency={currency}
          handleOnClickCallToAction={handleOnClickCallToAction}
          onAuthenticated={onAuthenticated}
          unitPrice={unitPrice}
          disable={disable}
        />
      </div>
    </div>
  </div>
);

ProductAction.propTypes = {
  currency: PropTypes.string.isRequired,
  discountPercentage: PropTypes.string,
  handleOnClickCallToAction: PropTypes.func.isRequired,
  onAuthenticated: PropTypes.func,
  promoCode: PropTypes.string,
  promoTnc: PropTypes.string,
  unitPrice: PropTypes.number.isRequired,
};

const ConfigurableProductContainer = ({
  configs,
  data = {},
  handleOnClickCallToAction,
  onAuthenticated,
  params: {
    slug = '',
  } = {},
  setConfigsByKey,
  variableSteps: steps = [],
  selectedSku,
  skuUnitPrice,
  ...props
}) => {
  const {
    currency,
    productLabel = '',
    description = '',
    gallery = [],
    details = [],
    video,
    discountPercentage,
    promoCode,
    promoTnc,
  } = data;

  const showFSTBadge = (variants, selectedSku) => {
    if (
      variants
      && selectedSku
      && variants.some(variant => variant.sku === selectedSku && variant.freeShippingTierEnabled)
    ) {
      return <FreeShippingTierBadge {...props} />;
    }
  }

  return (
    <div>
      <ConfigurableProductPageBreadCrumb
        name={productLabel}
        path={`configurable-product/${slug}`} {...props}/>
      <div className="StepViewContent">
        <div className="configurable-product__container container">
          <div className="row">
            <div className="col-12 col-sm-6">
              <div className="configurable-product__gallery">
                <GalleryCarouselWithPreview images={gallery} video={video}/>
              </div>
              {
                description && <div className="configurable-product__description"
                                    dangerouslySetInnerHTML={{ __html: `${description}` }}
                />
              }
            </div>
            <div className="right-column col-12 col-sm-6">
              <h1 className="configurable-product__title">{productLabel}</h1>
              <ConfigurableProductForm
                formValues={configs}
                setFormValues={setConfigsByKey}
                steps={steps}/>
              {details && details.length > 0 && <ProductDetail details={details}/>}
              <hr className="bg-gray-lighten-2"/>
              <ProductAction
                currency={currency}
                discountPercentage={discountPercentage}
                handleOnClickCallToAction={handleOnClickCallToAction}
                onAuthenticated={onAuthenticated}
                promoCode={promoCode}
                promoTnc={promoTnc}
                unitPrice={skuUnitPrice}
                disable={!selectedSku}
                {...props}
              />
              {showFSTBadge(data.variants, selectedSku)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const validate = (configs = {}, rules = {}) => new Promise((resolve, reject) => {
  const errors = Object
    .keys(configs)
    .reduce((carry, code) => {
      if (rules[code] === undefined || validateFormField(configs[code], rules[code])) {
        return carry;
      }

      return {
        ...carry,
        [code]: [`${rules[code].displayName} is invalid`],
      };
    }, {});

  if (Object.keys(errors).length > 0) {
    reject(errors);
  } else {
    resolve(true);
  }
});

const enhance = compose(
  withTranslation('configurable_product'),
  withConfigurableProductData,
  mapProps(({
    data: {
      gallery = [],
      steps = [],
      ...data
    },
    ...props
  }) => ({
    ...props,
    data: {
      ...data,
      gallery: gallery.map((src, index) => ({
        id: index,
        src,
      })),
      steps,
    },
    rules: steps.reduce((carry, {
      code,
      displayName,
      minLength,
      maxLength,
      type,
    }) => ({
      ...carry,
      [code]: {
        displayName,
        minLength,
        maxLength,
        type,
      },
    }), {}),
  })),
  withStateHandlers({
    configs: {},
    selectedSku: null,
    skuUnitPrice: 0,
    variableSteps: [],
    productConfigData: {},
  }, {
    setConfigs: () => (configs = {}) => ({ configs }),
    setConfigsByKey: ({ configs }) => (key, value) => ({
      configs: {
        ...configs,
        [key]: value,
      },
    }),
    setSelectedSku: () => selectedSku => ({ selectedSku }),
    setSkuUnitPrice: () => skuUnitPrice => ({ skuUnitPrice }),
    setVariableSteps: () => steps => ({ variableSteps: steps }),
    setProductConfigData: () => (productConfigData = {}) => ({ productConfigData }),
  }),
  withHandlers({
    setInitialConfigs: ({
      location: {
        query = {},
      } = {},
      setConfigsByKey,
      variableSteps = [],
    }) => () => {
      let skus = [];

      variableSteps.forEach(({ code, options = [], type }) => {
        let value = '';

        switch (type) {
          case 'text':
            value = query[code] || '';
            break;
          default: {
            const callback = query[code] !== undefined
              ? (({ id }) => query[code] == id) // eslint-disable-line eqeqeq
              : (({ hasVariants = false, variants = [] }) => !hasVariants
                || (!_.isEmpty(variants) && _.isEmpty(skus))
                || !_.isEmpty(skus.filter(sku => variants.includes(sku))));
            const { id, variants = [] } = options.find(callback) || {};

            value = id;
            skus = (!_.isEmpty(skus))
              ? skus.filter(sku => _.isEmpty(variants) || variants.includes(sku))
              : variants;
          }
        }

        setConfigsByKey(code, value);
      });
    },
    handleOnClickCallToAction: ({ data: { productCode } = {}, configs, rules, selectedSku }) => () => {
      validate(configs, rules)
        .then(() => new Promise((resolve, reject) => {
          addConfigurableProductToCart(productCode, configs, selectedSku)
            .done(({ data: { redirect_to_cart_url } = {} }) => {
              resolve(redirect_to_cart_url);
            })
            .fail(({ responseJSON: { errors = '' } }) => {
              reject(errors);
            });
        }))
        .then((cartUrl) => {
          window.location = cartUrl;
        })
        .catch(errors => showErrorMsg(errors));
    },
  }),
  withHandlers({
    onAuthenticated: ({ handleOnClickCallToAction }) => () => {
      handleOnClickCallToAction();
    },
  }),
  withDataLayerProductDetailView,
  withHandlers({
    sendProductDetailView: props => () => {
      const {
        data: {
          currency = '',
          details = [],
          productLabel = '',
          variants = []
        } = {},
        dataLayerProductDetailView,
        productConfigData,
        selectedSku
      } = props;
      const selectedVariant = variants.find(variant => variant.sku === selectedSku);
      const {
        productConfig,
        productName,
        productCategory
      } = productConfigData;
      let numberOfPages = null;
      let pricePerPage = null;
      const pages = details.find(detail => detail.header.toLowerCase().includes('pages'));
      if (pages !== undefined) {
        numberOfPages = Number(_.get(pages.value.match(/\d+/), '0'));
        pricePerPage = roundCurrency(selectedVariant.unitPrice / numberOfPages);
      }
      dataLayerProductDetailView({
        id: productConfig,
        currency,
        name: productLabel,
        numberOfPages,
        price: selectedVariant.unitPrice,
        pricePerPage,
        productName,
        category: productCategory,
        variant: selectedSku
      });
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        data: {
          steps = [],
          productCode = ''
        } = {},
        setInitialConfigs,
        setProductConfigData,
        setVariableSteps,
        sendProductDetailView
      } = this.props;

      fetchPcpProductConfigsData([productCode])
        .done((response) => {
          if (_.get(response.data, '0')) {
            const {
              product_category: productCategory,
              product_config: productConfig,
              product_name: productName
            } = _.get(response.data, '0');
            setProductConfigData({
              productCategory,
              productConfig,
              productName
            });
            sendProductDetailView();
          }
        });
      new Promise(resolve => resolve(setVariableSteps(steps)))
        .then(() => setInitialConfigs());
    },
    componentDidUpdate({ configs: prevConfigs }) {
      const {
        configs,
        data: { variants = [] } = {},
        productConfigData,
        selectedSku,
        sendProductDetailView,
        setSelectedSku,
        setSkuUnitPrice,
        setVariableSteps,
        variableSteps,
      } = this.props;

      if (!_.isEmpty(configs) && !_.isEqual(prevConfigs, configs)) {
        /* eslint-disable eqeqeq */
        const updatedKey = Object
          .keys(configs)
          .find(key => !prevConfigs[key] || prevConfigs[key] != configs[key]);
        /* eslint-enable eqeqeq */
        if (updatedKey !== undefined) {
          new Promise((resolve) => {
            resolve([
              setSelectedSku(null),
              setVariableSteps(refreshVariableSteps(configs, variableSteps)),
            ]);
          })
            .then(() => getSelectedSku(configs, variableSteps))
            .then((sku) => {
              setSelectedSku(sku);
              if (selectedSku !== sku && Object.keys(productConfigData).length > 0) {
                sendProductDetailView();
              }
              return getSkuUnitPrice(sku, variants);
            })
            .then(unitPrice => setSkuUnitPrice(unitPrice))
            .catch(() => {
              setSelectedSku(null);
              setSkuUnitPrice(0);
            });
        }
      }
    },
  }),
);

export default enhance(ConfigurableProductContainer);
export const undecorated = ConfigurableProductContainer;



// WEBPACK FOOTER //
// ./src/ConfigurableProduct/ConfigurableProductContainer.js